import { useRef, useEffect } from 'react';

/**
 * Declarative setInterval React hook.
 *
 * `useInterval` uses the same API as setInterval, but the arguments of this hook is "dynamic".
 * Set your `callback` function as the first argument and the `delay` as the second argument.
 * When passing `null` or `undefined` to the `delay` arg then it will stop/pause the interval.
 *
 * For implementation details explanation, why to use useInterval hook, and an example see Dan Abramov's blog post
 * 👀: https://overreacted.io/making-setinterval-declarative-with-react-hooks/
 * */
export const useInterval = (callback, delay) => {
  const savedCallback = useRef();

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    // If no delay then pause/stop the interval.
    // Delay of 0 is valid delay.
    if ((!delay && delay !== 0) || delay < 0) {
      return;
    }

    const id = setInterval(() => savedCallback.current(), delay);
    // eslint-disable-next-line consistent-return
    return () => clearInterval(id);
  }, [delay]);
};

export default useInterval;
